/**
 * Khối **website được phép làm nguồn** — ô nhập, danh sách, nút bỏ.
 *
 * Lynust chốt 10/09/2026: *"phải có ô cho tôi thêm dần các website"*, và **chỉ research tin mới
 * trong những website đã chỉ định**. Danh sách này làm hai việc cùng lúc, và màn hình phải nói cả
 * hai ra — nếu không nó trông như một tuỳ chọn:
 *
 * 1. **Hàng rào tin cậy** — URL ngoài danh sách không được trích dẫn (tầng X).
 * 2. **Nơi sinh chủ đề** — tin mới trên chính những domain này là đề bài của Luồng 3.
 *
 * Ba luật giao diện của khối này:
 *
 * - **Nút nằm trong `HangNut`**, không phải `ThanhHanhDong` — thanh kia mang `sm:hidden` nên vô
 *   hình trên desktop (`P-11`, đã xảy ra ở hai màn cùng lúc).
 * - **Ba câu trả lời khi thêm**, không phải một: *đã thêm* · *đã có rồi* · *không nhận ra domain*.
 *   Nói "đã thêm" cho một domain trùng là để người dùng đi tìm một dòng không tồn tại.
 * - **Danh sách rỗng ⇒ nói thẳng Luồng 3 đang DỪNG**, kèm lý do. Một luồng dừng im lặng trông
 *   giống hệt một luồng không có việc để làm (`AP-31`).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { NHAN_TANG, SITE_NGHIEN_CUU, UOC_TIEN_MOT_LUOT_RESEARCH_USD } from "@autoime/shared";

import { api, type KetQuaQuetDomain, type KetQuaResearch, type MucNguonUI } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import { useToast } from "../thanh-phan/toast.tsx";
import {
  Bao,
  Chip,
  DangTai,
  DauThe,
  HangNut,
  Khoi,
  Nut,
  The,
  Truong,
  ViSao,
  lopO,
} from "../thanh-phan/giao-dien.tsx";

/**
 * Nhãn tầng đọc từ `@autoime/shared`, **không viết tay lại**. Màn này và danh sách báo cáo ngay
 * bên dưới nói về cùng một con số; hai bản sao là hai câu sẽ lệch nhau.
 */
const TANG = ([1, 2, 3, 4, 5] as const).map((so) => ({ so, nhan: `${so} — ${NHAN_TANG[so]}` }));

const MAU_TANG: Record<number, "on" | "canh" | "thuong"> = {
  1: "on",
  2: "on",
  3: "on",
  4: "thuong",
  5: "canh",
};

export function KhoiNguonTinCay() {
  const kho = useQueryClient();
  const toast = useToast();
  const { data, isPending, error } = useQuery({
    queryKey: khoa.nguonTinCay,
    queryFn: api.nguonTinCay,
  });

  const [domain, datDomain] = useState("");
  const [tang, datTang] = useState<1 | 2 | 3 | 4 | 5>(1);
  const [ghiChu, datGhiChu] = useState("");
  /** `""` = mọi site chạy Luồng 3. Gửi lên thành `null`. */
  const [site, datSite] = useState<string>("");

  const lamMoi = () => kho.invalidateQueries({ queryKey: khoa.nguonTinCay });

  const them = useMutation({
    mutationFn: () => api.nguonTinCayThem(domain, tang, ghiChu, site === "" ? null : site),
    onSuccess: (kq) => {
      // Ba nhánh, ba câu. Đây là chỗ một cái toast "xong" duy nhất sẽ nói dối hai lần trong ba.
      if (kq.loai === "them") {
        toast.on("Đã thêm", `${kq.domain} — tầng ${tang}. Áp cho lượt research kế tiếp.`);
        datDomain("");
        datGhiChu("");
        void lamMoi();
      } else if (kq.loai === "da-co") {
        toast.bao("thuong", "Đã có trong danh sách", `${kq.domain} — không thêm dòng thứ hai.`);
      } else {
        toast.loi("Không nhận ra domain", kq.lyDo ?? undefined);
      }
    },
    onError: (loi: Error) => toast.loi("Không thêm được", loi.message),
  });

  const bo = useMutation({
    mutationFn: (d: string) => api.nguonTinCayBo(d),
    onSuccess: (kq) => {
      if (kq.daBo) {
        toast.bao(
          "thuong",
          `Đã bỏ ${kq.domain}`,
          "Bài đã viết KHÔNG đổi — danh sách chỉ áp cho lượt research kế tiếp.",
        );
        void lamMoi();
      } else {
        toast.loi("Không bỏ được", kq.lyDo ?? undefined);
      }
    },
    onError: (loi: Error) => toast.loi("Không bỏ được", loi.message),
  });

  /**
   * Quét tin mới — chạy tay một lượt đọc feed.
   *
   * Kết quả **giữ lại trên màn** cho tới khi bấm lại: một bảng biến mất sau ba giây là một bảng
   * không đọc kịp, và đây đúng là bảng người ta cần đọc từng dòng (domain nào im lặng).
   */
  const [ketQuaQuet, datKetQuaQuet] = useState<{
    ds: KetQuaQuetDomain[];
    tongChuDeTao: number;
    boQua: string | null;
  } | null>(null);

  const quet = useMutation({
    mutationFn: () => api.docTinQuet(),
    onSuccess: (kq) => {
      datKetQuaQuet(kq);
      const hong = kq.ds.filter((d) => d.loi !== null).length;
      if (kq.boQua) toast.loi("Chưa quét xong", kq.boQua);
      else if (kq.tongChuDeTao > 0)
        toast.on(
          `${kq.tongChuDeTao} chủ đề mới`,
          `Đọc ${kq.ds.length - hong}/${kq.ds.length} nguồn.`,
        );
      // ⚠️ BA ca, không phải hai. "Mọi nguồn đều hỏng" mà báo "đã đọc N nguồn" là một câu SAI
      // trên màn hình — đúng thứ `baoNeuCanhBao` đã phân biệt cho Telegram mà màn hình thì không.
      else if (hong === kq.ds.length && hong > 0)
        toast.loi(`Cả ${hong} nguồn đều hỏng`, "Không đọc được nguồn nào — xem cột feed bên dưới.");
      else
        toast.bao(
          "thuong",
          "Quét xong — không có tin mới",
          `Đọc được ${kq.ds.length - hong}/${kq.ds.length} nguồn.`,
        );
    },
    onError: (loi: Error) => toast.loi("Quét hỏng", loi.message),
  });

  /**
   * ⚠️ **Nút TỐN TIỀN duy nhất trên màn này.**
   *
   * Nó là `kieu="chinh"` — mảng cam, và màn này chỉ có **một** cái. Cam mang đúng một nghĩa
   * trong cả app: *có việc cần Lynust*. Ở đây nghĩa đó là *"bấm là tiêu tiền thật"*.
   */
  const [ketQuaResearch, datKetQuaResearch] = useState<KetQuaResearch[] | null>(null);

  const research = useMutation({
    mutationFn: () => api.researchChay(),
    onSuccess: (kq) => {
      datKetQuaResearch(kq.ds);
      const chay = kq.ds.filter((r) => r.daChay);
      if (chay.length === 0)
        toast.bao("thuong", "Không site nào chạy", kq.ds.map((r) => `${r.site}: ${r.boQua}`).join(" · "));
      else
        toast.on(
          `${chay.length} bài tổng hợp`,
          chay.map((r) => `${r.site}: ${r.soChuDeGop} tin, ${r.soTrichDan} trích dẫn`).join(" · "),
        );
      void lamMoi();
    },
    onError: (loi: Error) => toast.loi("Research hỏng", loi.message),
  });

  const dangBan = them.isPending || bo.isPending || quet.isPending || research.isPending;

  if (error) {
    return (
      <Bao muc="loi" tieuDe="Không tải được danh sách nguồn">
        {error.message}
      </Bao>
    );
  }
  if (isPending) return <DangTai />;

  return (
    <The vien={data.luong3ChayDuoc ? "thuong" : "canh"}>
      <DauThe
        tieuDe="Website được phép làm nguồn"
        phu="Vừa là hàng rào tin cậy, vừa là nơi Luồng 3 lấy đề bài. Chỉ những trang ở đây mới được đọc và trích dẫn."
      >
        <Chip muc={data.luong3ChayDuoc ? "on" : "loi"}>
          {data.ds.length} nguồn
        </Chip>
      </DauThe>

      {!data.luong3ChayDuoc && (
        <Bao muc="canh" tieuDe="Luồng 3 đang DỪNG">
          {data.lyDoDung ??
            "Danh sách nguồn tin cậy đang rỗng — Luồng 3 dừng cho tới khi có ít nhất một nguồn."}{" "}
          Đây là <strong>chủ ý</strong>: rỗng không có nghĩa là chấp nhận mọi nguồn. Đó đúng là cách
          blog bán hàng và nội dung do AI viết lọt vào một bài đứng tên công ty thật.
        </Bao>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_1fr]">
        <Truong nhan="Địa chỉ website" goiY="Dán cả URL đầy đủ cũng được — hệ tự lấy ra tên miền.">
          <input
            className={lopO}
            value={domain}
            disabled={dangBan}
            placeholder="moh.gov.vn"
            spellCheck={false}
            onChange={(e) => datDomain(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && domain.trim() !== "" && !dangBan) them.mutate();
            }}
          />
        </Truong>

        <Truong nhan="Tầng tin cậy" goiY="Tầng quyết định trọng số khi bài trích dẫn nguồn này.">
          <select
            className={lopO}
            value={tang}
            disabled={dangBan}
            onChange={(e) => datTang(Number(e.target.value) as 1 | 2 | 3 | 4 | 5)}
          >
            {TANG.map((t) => (
              <option key={t.so} value={t.so}>
                {t.nhan}
              </option>
            ))}
          </select>
        </Truong>
      </div>

      <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_1fr]">
        <Truong nhan="Ghi chú" goiY="Vì sao tin trang này. Ba tháng sau sẽ có người hỏi.">
          <input
            className={lopO}
            value={ghiChu}
            disabled={dangBan}
            placeholder="Bộ Y tế"
            onChange={(e) => datGhiChu(e.target.value)}
          />
        </Truong>

        <Truong
          nhan="Dùng cho site"
          goiY="miradry gần như chỉ có nguồn nước ngoài; company là thiết bị y tế Việt Nam."
        >
          <select
            className={lopO}
            value={site}
            disabled={dangBan}
            onChange={(e) => datSite(e.target.value)}
          >
            <option value="">cả hai site nghiên cứu</option>
            {SITE_NGHIEN_CUU.map((s) => (
              <option key={s} value={s}>
                chỉ {s}
              </option>
            ))}
          </select>
        </Truong>
      </div>

      {/* ⚠️ HangNut, KHÔNG phải ThanhHanhDong — thanh kia sm:hidden, vô hình trên desktop (P-11). */}
      <HangNut>
        <Nut
          kieu="vien"
          dangChay={them.isPending}
          disabled={dangBan || domain.trim() === ""}
          onClick={() => them.mutate()}
        >
          Thêm vào danh sách
        </Nut>
        <Nut
          kieu="phang"
          dangChay={quet.isPending}
          disabled={dangBan || !data.luong3ChayDuoc}
          onClick={() => quet.mutate()}
        >
          Quét tin mới
        </Nut>
        <Nut
          kieu="chinh"
          dangChay={research.isPending}
          disabled={dangBan || !data.luong3ChayDuoc}
          onClick={() => research.mutate()}
        >
          Research ngay
        </Nut>
        <span className="text-xs text-muc-500">
          Quét chỉ tải feed — <strong>miễn phí</strong>. Research{" "}
          <strong>tốn tiền thật</strong>, ước ~${UOC_TIEN_MOT_LUOT_RESEARCH_USD} mỗi site.
        </span>
      </HangNut>

      {ketQuaResearch && (
        <ul className="mt-3 space-y-1 text-xs">
          {ketQuaResearch.map((r) => (
            <li key={r.site} className={r.daChay ? "text-on-700" : "text-muc-600"}>
              <strong>{r.site}</strong>{" "}
              {r.daChay
                ? `— gộp ${r.soChuDeGop} tin, đọc ${r.soNguonDocDuoc} nguồn, ${r.soTrichDan} trích dẫn`
                : `— không chạy: ${r.boQua}`}
            </li>
          ))}
        </ul>
      )}

      {ketQuaQuet && ketQuaQuet.ds.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[34rem] text-left text-xs">
            <thead className="text-muc-500">
              <tr>
                <th className="py-1 pr-2 font-medium">nguồn</th>
                <th className="py-1 pr-2 font-medium">site</th>
                <th className="py-1 pr-2 font-medium">feed</th>
                <th className="py-1 pr-2 text-right font-medium">đọc</th>
                <th className="py-1 pr-2 text-right font-medium">mới</th>
                <th className="py-1 text-right font-medium">tạo</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-muc-200">
              {ketQuaQuet.ds.map((d, i) => (
                <tr key={`${d.site}-${d.domain}-${i}`} className={d.loi ? "text-loi-600" : ""}>
                  <td className="py-1 pr-2">{d.domain}</td>
                  <td className="py-1 pr-2 text-muc-500">{d.site}</td>
                  <td className="py-1 pr-2 text-muc-500" title={d.loi ?? d.duongFeed ?? ""}>
                    {d.loi ? d.loi : (d.duongFeed ?? "—")}
                  </td>
                  <td className="py-1 pr-2 text-right tabular-nums">{d.soTinDocDuoc}</td>
                  <td className="py-1 pr-2 text-right tabular-nums" title={d.daCat ? "vượt trần, chưa lấy hết" : ""}>
                    {d.soTinMoi}
                    {d.daCat && <span className="text-canh-600"> ▸</span>}
                  </td>
                  <td className="py-1 text-right tabular-nums">{d.soChuDeTao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.goiY.length > 0 && (
        <div className="mt-4">
          <p className="text-xs font-medium text-muc-600">
            Gợi ý — bấm để điền vào ô, <strong>không</strong> tự thêm:
          </p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {data.goiY.map((g) => (
              <button
                key={g.domain}
                type="button"
                disabled={dangBan}
                className="rounded-btn border border-muc-300 bg-muc-0 px-2 py-1 text-xs text-muc-700 transition-[transform,background-color] duration-100 hover:bg-muc-100 active:enabled:scale-[0.97] disabled:opacity-50"
                onClick={() => {
                  datDomain(g.domain);
                  datTang(g.tang);
                  datGhiChu(g.ghiChu);
                }}
              >
                {g.domain} <span className="text-muc-400">· tầng {g.tang}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {data.ds.length > 0 && (
        <ul className="mt-4 divide-y divide-muc-200 border-t border-muc-200">
          {data.ds.map((n: MucNguonUI) => (
            <li key={n.domain} className="flex min-w-0 flex-wrap items-center gap-2 py-2">
              <span className="min-w-0 flex-1 truncate text-sm text-muc-800" title={n.domain}>
                {n.domain}
              </span>
              <Chip muc={MAU_TANG[n.tang] ?? "thuong"}>tầng {n.tang}</Chip>
              <Chip muc="thuong">{n.site ? n.site : "cả hai site"}</Chip>
              {n.ghiChu && (
                <span className="max-w-[14rem] truncate text-xs text-muc-500" title={n.ghiChu}>
                  {n.ghiChu}
                </span>
              )}
              <span className="text-xs text-muc-400">{gioVN(n.ngayThem)}</span>
              <Nut
                kieu="phang"
                dangChay={bo.isPending && bo.variables === n.domain}
                disabled={dangBan}
                onClick={() => bo.mutate(n.domain)}
              >
                Bỏ
              </Nut>
            </li>
          ))}
        </ul>
      )}

      {data.nhatKy.length > 0 && (
        <Khoi nhan="Nhật ký thêm / bỏ" id="nhat-ky-nguon" tomTat={`${data.nhatKy.length} thay đổi gần đây`}>
          <ul className="space-y-1 text-xs text-muc-600">
            {data.nhatKy.map((d, i) => (
              <li key={`${d.luc}-${i}`} className="flex flex-wrap gap-2">
                <span className="text-muc-400">{gioVN(d.luc)}</span>
                <span className={d.viec === "them" ? "text-on-700" : "text-loi-600"}>
                  {d.viec === "them" ? "thêm" : "bỏ"}
                </span>
                <span className="font-medium text-muc-800">{d.domain}</span>
                {d.tang !== null && <span>tầng {d.tang}</span>}
                <span className="text-muc-400">— {d.nguoi}</span>
              </li>
            ))}
          </ul>
        </Khoi>
      )}

      <ViSao nhan="bỏ một nguồn thì sao">
        Bài <strong>đã viết</strong> giữ nguyên trích dẫn của nó. Danh sách chỉ áp cho lượt research
        <strong> kế tiếp</strong> — nếu không, bỏ một domain sẽ làm hàng chục bài cũ trông như thiếu
        nguồn.
      </ViSao>
    </The>
  );
}
